import { AskCitation } from '../../features/document-ocr/document-ocr.models';

export interface ChatMessageLine {
  text: string;
  citations: AskCitation[];
  /** True when the line came in with a bullet marker ("- ", "* ", "• ") — rag-service.js's
   * system prompt instructs the model to format every answer this way. The marker itself is
   * stripped from `text`; real <ul>/<li> markup renders the bullet instead (see
   * groupMessageLinesIntoBlocks), so this literal character never reaches the screen. */
  isBullet: boolean;
}

export type ChatMessageBlock =
  | { type: 'list'; items: ChatMessageLine[] }
  | { type: 'text'; line: ChatMessageLine };

const CASE_MARKER_RE = /\{\{CASE:([\d,\s]+)\}\}/g;
const BULLET_PREFIX_RE = /^[-*•‣◦⁃]\s+/;

/**
 * Splits an assistant answer into its bullet lines, pulling each line's own {{CASE:id,id}}
 * marker(s) — emitted by rag-service.js, one per bullet — into that line's citations. This is
 * what puts the case card directly under the point it supports instead of dumping every cited
 * case in one block at the end of the message, disconnected from which point it backs up.
 */
export function parseMessageLines(text: string, citations: AskCitation[] = []): ChatMessageLine[] {
  const byId = new Map(citations.map((c) => [Number(c.grievanceId), c]));
  // A case cited across several consecutive bullets (the model repeating the same
  // {{CASE:id}} marker on each) only gets its card shown the first time — repeating an
  // identical link under every one of those bullets is just noise, not new information.
  const alreadyShown = new Set<number>();
  return String(text || '')
    .split('\n')
    .map((line) => {
      const ids: number[] = [];
      const cleaned = line
        .replace(CASE_MARKER_RE, (_match, idsStr: string) => {
          for (const part of idsStr.split(',')) {
            const id = Number(part.trim());
            if (Number.isFinite(id)) ids.push(id);
          }
          return '';
        })
        .trimEnd();
      const bulletMatch = cleaned.match(BULLET_PREFIX_RE);
      const displayText = bulletMatch ? cleaned.slice(bulletMatch[0].length) : cleaned;
      const lineCitations = ids
        .filter((id) => !alreadyShown.has(id))
        .map((id) => {
          alreadyShown.add(id);
          return byId.get(id);
        })
        .filter((c): c is AskCitation => c != null);
      return { text: displayText, citations: lineCitations, isBullet: bulletMatch != null };
    })
    .filter((line) => line.text.length > 0 || line.citations.length > 0);
}

/**
 * Groups consecutive bullet lines into a single list block so they render as one real <ul>
 * (each line's own citation card nested inside its <li>) instead of every "- " reaching the
 * screen as a literal character in front of a plain paragraph.
 */
export function groupMessageLinesIntoBlocks(lines: ChatMessageLine[]): ChatMessageBlock[] {
  const blocks: ChatMessageBlock[] = [];
  for (const line of lines) {
    if (line.isBullet) {
      const last = blocks[blocks.length - 1];
      if (last?.type === 'list') {
        last.items.push(line);
      } else {
        blocks.push({ type: 'list', items: [line] });
      }
    } else {
      blocks.push({ type: 'text', line });
    }
  }
  return blocks;
}
